import { useState } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { PageCard } from '../../../Compounds/CardWithTitle';
import { AccountSelectionForm } from '../../../Compounds/UserCreation';
import { RiskConfigurationForm } from '../../../Compounds/ProjectManagementConfig/RiskConfigurationForm';
import { CustomFieldsConfigurationForm } from '../../../Compounds/ProjectManagementConfig/CustomFieldsConfigurationForm';
import { TableCustomizationsConfigurationForm } from '../../../Compounds/ProjectManagementConfig/TableCustomizationsConfigurationForm';
import { Account } from '../../../../types';
import { getUser } from '../../../../store/User';

const CONFIGURATION_OPTIONS = ['Risk', 'Custom Fields', 'Table Customizations'];

export const ProjectManagementConfigTab = () => {
  const { accounts } = getUser();
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  const [selectedConfiguration, setSelectedConfiguration] = useState<string | null>(null);

  const handleAccountChange = (account: Account | undefined) => {
    setSelectedAccount(account || null);
  };

  const handleConfigurationChange = (_e: React.SyntheticEvent, value: string | null) => {
    setSelectedConfiguration(value);
  };

  // Closing a form clears the configuration selection, returning to the base state.
  const handleClose = () => setSelectedConfiguration(null);

  return (
    <>
      <PageCard
        title="Project Management Configuration"
        dataCy="pm-config-tab"
        enableCollapse={false}>
        <div className="flex items-center justify-between mb-4 pr-8">
          <div className="flex items-center gap-24">
            <div className="flex flex-col">
              <label
                htmlFor="user-account-selection-input"
                className="text-sm font-medium text-gray-700 mb-1">
                Account:
              </label>
              <AccountSelectionForm
                accounts={accounts}
                selectedAccount={selectedAccount}
                setSelectedAccount={handleAccountChange}
                showLabel={false}
                openOnFocus
                styles="w-64"
                inputStyles="h-[1.75rem]"
              />
            </div>
            <div className="flex flex-col">
              <label
                htmlFor="pm-config-type"
                className="text-sm font-medium text-gray-700 mb-1">
                Configuration:
              </label>
              <Autocomplete
                id="pm-config-type"
                value={selectedConfiguration}
                className="w-64"
                onChange={handleConfigurationChange}
                onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
                options={CONFIGURATION_OPTIONS}
                getOptionLabel={(option: string) => option}
                size="small"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    InputProps={{ ...params.InputProps, style: { fontSize: '.875rem' } }}
                    label=""
                    className="h-[1.75rem]"
                    data-cy="pm-config-type-dropdown"
                    placeholder="Select config."
                  />
                )}
                renderOption={(props, option) => (
                  <li
                    {...props}
                    key={option}
                    className="text-sm14 py-1 pl-4 cursor-pointer">
                    {option}
                  </li>
                )}
              />
            </div>
          </div>
        </div>
      </PageCard>
      {selectedAccount && selectedConfiguration === 'Risk' && (
        <RiskConfigurationForm
          key={selectedAccount.id}
          account={selectedAccount}
          onClose={handleClose}
        />
      )}
      {selectedAccount && selectedConfiguration === 'Custom Fields' && (
        <CustomFieldsConfigurationForm
          key={selectedAccount.id}
          account={selectedAccount}
          onClose={handleClose}
        />
      )}
      {selectedAccount && selectedConfiguration === 'Table Customizations' && (
        <TableCustomizationsConfigurationForm
          key={selectedAccount.id}
          account={selectedAccount}
          onClose={handleClose}
        />
      )}
    </>
  );
};
